import * as React from 'react';
import styled, { css } from 'styled-components';

import { size } from '../layout/helpers';
import { fontRegular, fontBold } from '../layout/font';
import { UtilityTextRight } from '../Utility';

export const ButtonReset = styled.button.attrs(() => ({
  type: 'button',
}))`
  display: inline-block;
  vertical-align: middle;
  padding: 0;
  margin: 0;
  background-color: transparent;
  border: 0;
  border-radius: 0;
  font-family: sans-serif;
  font-weight: 400;
  font-size: 1em;
  color: currentColor;
  line-height: 1.2;
  text-align: center;
  text-decoration: none;
  -webkit-tap-highlight-color: rgba(255, 255, 255, 0.1);
  cursor: pointer;
`;

type ButtonResetProps = React.DetailedHTMLProps<
  React.HTMLAttributes<HTMLButtonElement>,
  HTMLButtonElement
> & {
  fullWidth?: boolean;
  inputHeight?: boolean;
  $breakWord?: boolean;
};

const ButtonBase = styled(ButtonReset)<ButtonResetProps>`
  ${fontBold};
  position: relative;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 14px 48px;
  border-radius: var(--button-border-radius, 3px);
  font-size: 1rem;
  line-height: 1.2;
  text-decoration: none;
  transform-origin: 50% 50%;
  transition: transform 300ms cubic-bezier(0.47, 1.64, 0.41, 0.8);

  &:before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    background-color: rgba(0, 0, 0, 0.04);
    border-radius: inherit;
    opacity: 0;
    transition: opacity 150ms ease;
  }

  &:hover {
    &:before {
      opacity: 1;
    }
  }

  &:active {
    transform: scale(0.97);
  }

  &[disabled] {
    background-color: #ddd;
    border-color: #ddd;
    color: #717171;
  }

  ${({ fullWidth }) =>
    fullWidth &&
    css`
      display: flex;
      width: 100%;
    `}

  ${({ inputHeight }) =>
    inputHeight &&
    css`
      height: 48px;
    `}

  ${({ $breakWord }) =>
    $breakWord &&
    css`
      word-break: break-word;
      hyphens: auto;
    `}

  ${(props) => props.theme.breakpoint.LtSm} {
    padding: 14px 20px;
    font-size: 0.875rem;
  }
`;

export const ButtonPrimary = styled(ButtonBase)`
  background-color: var(--button-primary-bg-color, ${(props) => props.theme.color.primary});
  color: var(--button-primary-text-color, ${(props) => props.theme.color.textLight});
`;

export const ButtonSecondary = styled(ButtonBase)`
  background-color: var(--button-secondary-bg-color, ${(props) => props.theme.color.accentDark});
  color: var(--button-secondary-text-color, ${(props) => props.theme.color.primary});
`;

export const ButtonContent = styled.div`
  position: relative;
  z-index: 1;

  & + & {
    margin-left: 4px;
  }
`;

export const ButtonClear = styled(ButtonReset)`
  ${fontBold};
  position: relative;
  font-size: 0.875rem;
  padding: ${size(1.5)};
  margin: ${size(-1.5)};
  text-align: left;
  color: var(--link-color, ${(props) => props.theme.color.link});
  transform-origin: 50% 50%;
  transition: transform 300ms cubic-bezier(0.47, 1.64, 0.41, 0.8);

  ${UtilityTextRight} & {
    text-align: right;
  }

  &:before {
    content: '';
    position: absolute;
    top: 0;
    bottom: 0;
    left: 0;
    right: 0;
    background-color: var(--element-accent-color, ${(props) => props.theme.color.accentDark});
    border-radius: var(--button-border-radius, 3px);
    opacity: 0;
    transition: opacity 200ms ease;
  }

  &:hover {
    &:before {
      opacity: 1;
    }
  }

  &:active {
    transform: scale(0.95);
  }
`;

type ButtonInlineProps = React.DetailedHTMLProps<
  React.HTMLAttributes<HTMLButtonElement>,
  HTMLButtonElement
> & {
  inline?: boolean;
  as?: string;
};

// Button with appearance of a link
export const ButtonInline = styled(ButtonReset)<ButtonInlineProps>`
  ${fontRegular};
  display: inline-flex;
  vertical-align: baseline;
  text-align: left;
  color: var(--link-color, ${(props) => props.theme.color.link});

  &:hover {
    text-decoration: underline;
  }

  ${({ inline }) =>
    inline &&
    css`
      display: inline;
    `}
`;

export const ButtonInlineBold = styled(ButtonInline)`
  ${fontBold};
`;
